export interface GoogleAnalyticsConfig {
  enabled: boolean
  measurementIds: string[]
}

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: GtagFunction
  }
}

type GtagFunction = (...args: unknown[]) => void

const MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]+$/i
const GTAG_SCRIPT_ID = 'cdt-ga-gtag-script'

export function normalizeMeasurementIds(ids?: string[]): string[] {
  if (!Array.isArray(ids)) return []
  const seen = new Set<string>()
  const result: string[] = []
  for (const raw of ids) {
    const id = String(raw ?? '').trim().toUpperCase()
    if (id && MEASUREMENT_ID_PATTERN.test(id) && !seen.has(id)) {
      seen.add(id)
      result.push(id)
    }
  }
  return result
}

export function parseMeasurementIdsText(text: string): string[] {
  return normalizeMeasurementIds(text.split(/[\n,]+/))
}

export function formatMeasurementIdsText(ids: string[]): string {
  return normalizeMeasurementIds(ids).join('\n')
}

export function parseGoogleAnalyticsConfig(raw?: string | null): GoogleAnalyticsConfig {
  if (!raw?.trim()) {
    return { enabled: false, measurementIds: [] }
  }

  try {
    const parsed = JSON.parse(raw) as Partial<GoogleAnalyticsConfig>
    return {
      enabled: parsed.enabled === true,
      measurementIds: normalizeMeasurementIds(parsed.measurementIds),
    }
  } catch {
    return { enabled: false, measurementIds: [] }
  }
}

let initialized = false
let blocked = true
let activeMeasurementIds: string[] = []
let scriptLoading: Promise<void> | null = null

export function ensureGtagStub(): void {
  window.dataLayer = window.dataLayer || []
  if (!window.gtag) {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer?.push(args)
    }
  }
}

export function loadGtagScript(primaryId: string): Promise<void> {
  if (document.getElementById(GTAG_SCRIPT_ID)) {
    return Promise.resolve()
  }

  if (scriptLoading) return scriptLoading

  scriptLoading = new Promise((resolve) => {
    const script = document.createElement('script')
    script.id = GTAG_SCRIPT_ID
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primaryId)}`
    script.onload = () => resolve()
    script.onerror = () => resolve()
    document.head.appendChild(script)
  })

  return scriptLoading
}

function updateAnalyticsConsent(granted: boolean): void {
  if (typeof window.gtag !== 'function') return
  window.gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
  })
}

export function setGoogleAnalyticsBlocked(value: boolean): void {
  blocked = value
  updateAnalyticsConsent(!blocked)
}

export function isGoogleAnalyticsInitialized(): boolean {
  return initialized
}

export async function initGoogleAnalytics(measurementIds: string[]): Promise<boolean> {
  if (!import.meta.client || blocked) return false

  const ids = normalizeMeasurementIds(measurementIds)
  if (!ids.length) return false

  ensureGtagStub()
  await loadGtagScript(ids[0])

  if (blocked) return false

  window.gtag?.('js', new Date())
  for (const id of ids) {
    window.gtag?.('config', id, {
      send_page_view: false,
      anonymize_ip: true,
    })
  }

  activeMeasurementIds = ids
  updateAnalyticsConsent(true)
  initialized = true
  return true
}

export function disableGoogleAnalytics(): void {
  setGoogleAnalyticsBlocked(true)
}

export function enableGoogleAnalytics(): void {
  setGoogleAnalyticsBlocked(false)
}

export function trackGaPageView(path: string): void {
  if (!import.meta.client || blocked || !initialized || typeof window.gtag !== 'function') return

  for (const id of activeMeasurementIds) {
    window.gtag('config', id, {
      page_path: path,
    })
  }

  window.gtag('event', 'page_view', {
    page_path: path,
  })
}

export function trackGaEvent(
  event: string,
  params?: Record<string, unknown>,
): void {
  if (!import.meta.client || blocked || !initialized || typeof window.gtag !== 'function') return
  window.gtag('event', event, params)
}
